import { useState } from "react";
import { isBenignUploadError } from "common/utils/app-monitoring/benign-upload-error";
import { notifyError } from "common/utils/app-monitoring/notify";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { hasGraphQLErrors } from "@design-lab/shared/lib/errorGuards";
import { DimensionLoadingError } from "@design-lab/shared/lib/errors";
import { useCompositionForm } from "@design-lab/entities/composition";
import { useAddToCartSubmit } from "./useAddToCartSubmit";

export function useAddToCartModal() {
  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const { handleAddToCart } = useAddToCartSubmit();

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleAddToCartClick = async (reenableButton: boolean, onSuccess: () => void) => {
    try {
      setIsAddingToCart(true);
      const formData = {
        artwork: artworkForm.getValues(),
        compositions: compositionForm.getValues()
      };
      await handleAddToCart(formData);
      onSuccess();
    } catch (error) {
      setIsAddingToCart(false);

      const artworkValues = artworkForm.getValues();
      const compositionValues = compositionForm.getValues();

      const graphQLErrors = hasGraphQLErrors(error) ? error.graphQLErrors : [];
      const errorMsg = error instanceof Error ? error.message : String(error);
      const errorName = error instanceof Error ? error.name : "Unknown";

      const sampleErrorCodes = new Set([
        "SAMPLE_ALREADY_IN_CART",
        "SAMPLE_ORDER_LIMIT_REACHED",
        "SAMPLE_LIMIT_ONE_PER_CART"
      ]);

      const hasSampleLimitGraphQLError = graphQLErrors.some(
        (e: { extensions?: { code?: string | number } }) =>
          sampleErrorCodes.has(String(e?.extensions?.code))
      );

      // Also check error message for sample-related errors (fallback if error codes not set)
      const errorMessage = error instanceof Error ? (error.message?.toLowerCase() ?? "") : "";
      const hasSampleMessageError = errorMessage.includes("sample");

      const isSampleError = hasSampleLimitGraphQLError || hasSampleMessageError;
      const isDimensionLoadingError = error instanceof DimensionLoadingError;
      const benign = isBenignUploadError({
        message: errorMsg,
        type: errorName,
        status: null
      });

      if (!isSampleError && !isDimensionLoadingError && !benign) {
        notifyError("Failed to add to cart modal", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            error_name: error instanceof Error ? error.name : "Unknown",
            stack_trace: error instanceof Error ? error.stack : null,
            hook: "useAddToCartModal",
            // Capture form data context for debugging
            formContext: {
              hasDesignProjectUuid: !!artworkValues?.designProjectUuid,
              designProjectUuid: artworkValues?.designProjectUuid || null,
              selectedFrontPlacement: artworkValues?.selectedFrontPlacement,
              hasFrontArtworkId: !!artworkValues?.front?.artworkId,
              hasBackArtworkId: !!artworkValues?.back?.artworkId,
              hasFrontLeftChestArtworkId: !!artworkValues?.frontLeftChest?.artworkId,
              compositionsCount: compositionValues?.addedCompositions?.length || 0,
              currentCompositionId: compositionValues?.currentCompositionId
            }
          }
        });
      }
      throw error;
    } finally {
      if (reenableButton) {
        setIsAddingToCart(false);
      }
    }
  };

  return {
    isAddingToCart,
    handleAddToCartClick
  };
}
